import type { Immutable } from "immer";
import React from "react";

// The legacy ``__react_component_tooltip`` trigger spans carry a non-standard
// ``currentitem="true"`` attribute (an artifact of the legacy react-tooltip
// server markup that the global JS tooltip lib rebinds to). It must be present
// VERBATIM in the SSR output. Rather than globally widen React's
// ``HTMLAttributes`` (a ``declare module "react"`` augmentation leaks across the
// whole repo -- the same global-type-pollution class as the prior TrustedHTML
// incident), spread this string-record onto the trigger span. React 19 passes
// the unknown lowercase attribute through to the DOM at runtime; the cast keeps
// the widening confined to this one prop (no ``as any``).
export const legacyTooltipAttrs = { currentitem: "true" } as Record<
    string,
    string
>;

/**
 * Inline icon SVGs for ``HardcodedBlockTemplate`` (#33404), transcribed VERBATIM
 * from the svgo-built ``static/dist2/svg-django/<src>.svg`` sources that the
 * legacy ``cms/components/svg.html`` include rendered server-side (the exact
 * source the screenshot baselines were generated from). Inline (not
 * ``react-inlinesvg``) so the markup is present in the SSR/fragment HTML --
 * ``react-inlinesvg`` fetches the SVG client-side on mount (requires
 * ``window``), so the icons would be ABSENT from the SSR/fragment HTML and only
 * appear after hydrate + async fetch (the SizeGuide ``svg.tsx`` precedent inlines
 * for exactly this reason).
 *
 * ``Svg`` reproduces the ``cms/components/svg.html`` wrapper behavior:
 *
 *   {% if description %}
 *       <span class="ada-screenreader-only">{{ description }}</span>
 *   {% endif %}
 *   {% include "svg-django/<src>.svg" %}
 *
 * i.e. a ``.ada-screenreader-only`` span SIBLING BEFORE the ``<svg>`` rendered
 * ONLY when a ``description`` arg was passed (truthy). The ``<svg>`` itself
 * carries: the ``class`` (always present; empty string when no ``class`` arg),
 * ``role="img"``, a ``<title>`` ONLY when a ``title`` arg was passed (truthy),
 * and ``aria-hidden="true"`` ONLY when ``ariaHidden=True`` was passed. The
 * legacy ``title_id`` arg is never passed by any of these partials, so the
 * ``aria-labelledby`` / ``id`` conditionals collapse away.
 */

// The per-icon inner SVG: receives the resolved svg-element attributes (class,
// title, aria-hidden) and emits the literal transcribed ``<svg>...`` markup.
type SvgElementProps = Immutable<{
    className: string;
    title?: string;
    titleId: string;
    ariaHidden: boolean;
}>;

type IconComponent = (props: SvgElementProps) => React.JSX.Element;

// Allyant SVG labeling: <title id> + aria-labelledby on the parent <svg>.
const Title = (
    props: Immutable<{ title?: string; titleId: string }>,
): React.JSX.Element | null =>
    props.title ? <title id={props.titleId}>{props.title}</title> : null;

export const CheckMark: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-labelledby={props.title ? props.titleId : undefined}
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        width="64"
        height="64"
        viewBox="0 0 72 72"
    >
        <Title title={props.title} titleId={props.titleId} />
        <path d="M57.658 12.643a4 4 0 0 1 1.183 5.532l-25.915 40a4 4 0 0 1-6.361.467L13.514 43.807a4.001 4.001 0 0 1 6.006-5.285l9.563 10.87 23.043-35.567a4 4 0 0 1 5.532-1.182" />
    </svg>
);

export const Dash: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-labelledby={props.title ? props.titleId : undefined}
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 14 14"
    >
        <Title title={props.title} titleId={props.titleId} />
        <text
            data-name="—"
            fontFamily="Gotham-Bold, Gotham"
            fontSize="14"
            fontWeight="700"
            transform="translate(7 11)"
        >
            <tspan x="-6" y="0">
                —
            </tspan>
        </text>
    </svg>
);

export const FacebookF: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-labelledby={props.title ? props.titleId : undefined}
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        width="1792"
        height="1792"
        viewBox="0 0 1792 1792"
    >
        <Title title={props.title} titleId={props.titleId} />
        <path d="M1343 12v264h-157q-86 0-116 36t-30 108v189h293l-39 296h-254v759H734V905H479V609h255V391q0-186 104-288.5T1115 0q147 0 228 12" />
    </svg>
);

export const LinkedinF: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-labelledby={props.title ? props.titleId : undefined}
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        style={{ color: "#fff" }}
        viewBox="0 0 448 512"
    >
        <Title title={props.title} titleId={props.titleId} />
        {/*! Font Awesome Free 6.1.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free (Icons: CC BY 4.0, Fonts: SIL OFL 1.1, Code: MIT License) Copyright 2022 Fonticons, Inc.*/}
        <path
            fill="#fff"
            d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3M447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z"
        />
    </svg>
);

export const TempurLogoTextBlack: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-labelledby={props.title ? props.titleId : undefined}
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="29.8 64 228 24"
    >
        <Title title={props.title} titleId={props.titleId} />
        <path
            d="M35.5 69h-5.7v-4.6h17.7V69h-5.8v18.7h-6.2zm13.7-4.6h16.4v4.4H55.4v4.8h9.7V78h-9.7v5.3H66v4.4H49.2zm19.1 0H78l5 15.5 5.2-15.5h9.4v23.3h-6.1l.1-17.9-6 17.9h-5.7l-5.6-17.9h-.1l.1 17.9h-6zm32.7 0h7.4c7.1 0 10.4 2.4 10.4 7.6 0 4.7-3.4 7.5-9.2 7.5h-2.4v8.1H101zm6.2 10.7h1.6c2.1 0 3.8-.8 3.8-3.2s-1.7-3.1-3.8-3.1h-1.6zm33.9 3.7c0 6.4-4.1 9.2-10.2 9.2s-10.2-2.9-10.2-9.2V64.4h6.2v13.4c0 3.1.8 5.7 4 5.7s4-2.6 4-5.7V64.4h6.2zm2.5-14.4h11.1c4.1 0 7.5 1.9 7.5 5.9 0 3.1-1.5 5.2-4.5 5.9v.1c2 .5 2.1 2.3 3.3 5.1l2.3 6.3h-6.5l-1.3-4.2c-1.3-4-2.1-5-4.4-5h-1.3v9.2h-6.2zm6.2 9.7h1.6c2 0 4.5-.1 4.5-2.7 0-2.2-2.2-2.6-4.5-2.6h-1.6zm25-9.7h6.1c4.8 0 8.7 1.7 8.7 7 0 5.2-4 7-8.2 7h-3.3v9.3h-3.3zm3.3 11h3.3c2.1 0 4.7-1 4.7-4.1 0-2.9-3.1-4-5.1-4h-2.8v8.1zm29.1-11h6.9c6.9 0 12.4 4.3 12.4 11.4 0 7.8-4.7 11.8-12.4 11.8h-6.9zm3.3 20.3h3.5c5.7 0 8.9-2.8 8.9-8.8 0-5.4-3.7-8.6-8.5-8.6h-3.9zm18.3-20.3h3.3v23.3h-3.3zm22.9 3.8c-1.5-.9-3.1-1.3-4.8-1.3-5.4 0-8.9 3.9-8.9 9.1 0 5.5 3.9 9.1 8.8 9.1 1.7 0 3.9-.4 5.1-1.1l.2 3.2c-1.8.7-3.9.8-5.3.8-7.4 0-12.3-4.7-12.3-12.1 0-7.2 5.2-11.9 12.3-11.9 1.8 0 3.6.3 5.2 1zM254 66c0-1 .8-1.9 1.9-1.9 1 0 1.9.8 1.9 1.9 0 1-.8 1.9-1.9 1.9-1 0-1.9-.8-1.9-1.9m3.4 0c0-.9-.6-1.5-1.4-1.5s-1.4.6-1.4 1.5.6 1.5 1.4 1.5c.7 0 1.4-.6 1.4-1.5m-2.2-1.1h.8c.5 0 .8.2.8.6s-.2.5-.6.6l.6.9h-.4l-.6-.9h-.2v.9h-.4zm.4 1h.4c.2 0 .4 0 .4-.3 0-.2-.2-.3-.4-.3h-.4zM163 75.3h9v3h-9zm28.7-10.9h13v2.9H195v6.8h8.8v3H195v7.6h9.7v3h-13z"
            className="st0"
        />
    </svg>
);

export const TempurLogoTextWhite: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-labelledby={props.title ? props.titleId : undefined}
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        id="tempur_logo_text_white"
        x="0"
        y="0"
        version="1.1"
        viewBox="29.8 64 228 24"
    >
        <Title title={props.title} titleId={props.titleId} />
        <style>{`#tempur_logo_text_white .st0{fill:#fff}`}</style>
        <g id="XMLID_1_">
            <g id="XMLID_2_">
                <g id="XMLID_3_">
                    <g id="XMLID_7_">
                        <path
                            id="XMLID_32_"
                            d="M35.5 69h-5.7v-4.6h17.7V69h-5.8v18.7h-6.2z"
                            className="st0"
                        />
                        <path
                            id="XMLID_31_"
                            d="M49.2 64.4h16.4v4.4H55.4v4.8h9.7V78h-9.7v5.3H66v4.4H49.2z"
                            className="st0"
                        />
                        <path
                            id="XMLID_30_"
                            d="M68.3 64.4H78l5 15.5 5.2-15.5h9.4v23.3h-6.1l.1-17.9-6 17.9h-5.7l-5.6-17.9h-.1l.1 17.9h-6z"
                            className="st0"
                        />
                        <path
                            id="XMLID_27_"
                            d="M101 64.4h7.4c7.1 0 10.4 2.4 10.4 7.6 0 4.7-3.4 7.5-9.2 7.5h-2.4v8.1H101zm6.2 10.7h1.6c2.1 0 3.8-.8 3.8-3.2s-1.7-3.1-3.8-3.1h-1.6z"
                            className="st0"
                        />
                        <path
                            id="XMLID_26_"
                            d="M141.1 78.8c0 6.4-4.1 9.2-10.2 9.2s-10.2-2.9-10.2-9.2V64.4h6.2v13.4c0 3.1.8 5.7 4 5.7s4-2.6 4-5.7V64.4h6.2z"
                            className="st0"
                        />
                        <path
                            id="XMLID_23_"
                            d="M143.6 64.4h11.1c4.1 0 7.5 1.9 7.5 5.9 0 3.1-1.5 5.2-4.5 5.9v.1c2 .5 2.1 2.3 3.3 5.1l2.3 6.3h-6.5l-1.3-4.2c-1.3-4-2.1-5-4.4-5h-1.3v9.2h-6.2zm6.2 9.7h1.6c2 0 4.5-.1 4.5-2.7 0-2.2-2.2-2.6-4.5-2.6h-1.6z"
                            className="st0"
                        />
                        <path
                            id="XMLID_20_"
                            d="M174.8 64.4h6.1c4.8 0 8.7 1.7 8.7 7 0 5.2-4 7-8.2 7h-3.3v9.3h-3.3zm3.3 11h3.3c2.1 0 4.7-1 4.7-4.1 0-2.9-3.1-4-5.1-4h-2.8v8.1z"
                            className="st0"
                        />
                        <path
                            id="XMLID_17_"
                            d="M207.2 64.4h6.9c6.9 0 12.4 4.3 12.4 11.4 0 7.8-4.7 11.8-12.4 11.8h-6.9zm3.3 20.3h3.5c5.7 0 8.9-2.8 8.9-8.8 0-5.4-3.7-8.6-8.5-8.6h-3.9z"
                            className="st0"
                        />
                        <path
                            id="XMLID_16_"
                            d="M228.8 64.4h3.3v23.3h-3.3z"
                            className="st0"
                        />
                        <path
                            id="XMLID_15_"
                            d="M251.7 68.2c-1.5-.9-3.1-1.3-4.8-1.3-5.4 0-8.9 3.9-8.9 9.1 0 5.5 3.9 9.1 8.8 9.1 1.7 0 3.9-.4 5.1-1.1l.2 3.2c-1.8.7-3.9.8-5.3.8-7.4 0-12.3-4.7-12.3-12.1 0-7.2 5.2-11.9 12.3-11.9 1.8 0 3.6.3 5.2 1z"
                            className="st0"
                        />
                        <path
                            id="XMLID_10_"
                            d="M254 66c0-1 .8-1.9 1.9-1.9 1 0 1.9.8 1.9 1.9 0 1-.8 1.9-1.9 1.9-1 0-1.9-.8-1.9-1.9m3.4 0c0-.9-.6-1.5-1.4-1.5s-1.4.6-1.4 1.5.6 1.5 1.4 1.5c.7 0 1.4-.6 1.4-1.5m-2.2-1.1h.8c.5 0 .8.2.8.6s-.2.5-.6.6l.6.9h-.4l-.6-.9h-.2v.9h-.4zm.4 1h.4c.2 0 .4 0 .4-.3 0-.2-.2-.3-.4-.3h-.4z"
                            className="st0"
                        />
                        <path
                            id="XMLID_9_"
                            d="M163 75.3h9v3h-9z"
                            className="st0"
                        />
                        <path
                            id="XMLID_8_"
                            d="M191.7 64.4h13v2.9H195v6.8h8.8v3H195v7.6h9.7v3h-13z"
                            className="st0"
                        />
                    </g>
                </g>
            </g>
        </g>
    </svg>
);

export const TempurLogotypeWhite: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-labelledby={props.title ? props.titleId : undefined}
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        data-name="Layer 1"
        viewBox="0 0 322.83 34.07"
    >
        <Title title={props.title} titleId={props.titleId} />
        <path
            fill="#fff"
            d="M8.1 7.08H0V.57h25v6.51h-8.12v26.43H8.1zM27.5.57h23.22V6.8H36.27v6.8h13.65v6.22H36.27v7.46h14.97v6.23H27.5zm26.95 0h13.82l6.99 21.89h.09L82.66.57h13.32v32.94h-8.6l.14-25.3h-.09l-8.4 25.3h-8.07l-8.02-25.3h-.1l.2 25.3h-8.59zm46.32-.02h10.47c10 0 14.77 3.4 14.77 10.8 0 6.66-4.86 10.62-13.07 10.62h-3.44v11.58h-8.78v-33Zm8.73 15.21h2.26c3 0 5.43-1.08 5.43-4.53s-2.41-4.43-5.43-4.43h-2.26v9Zm48 5.24c0 9-5.85 13.07-14.44 13.07S128.62 30.02 128.62 21V.55h8.78v19c0 4.34 1.18 8 5.66 8s5.66-3.68 5.66-8v-19h8.78v20.43ZM161.07.55h15.67c5.85 0 10.61 2.64 10.61 8.3 0 4.39-2.08 7.32-6.32 8.3v.1c2.84.76 3 3.26 4.67 7.27l3.21 9h-9.15l-1.88-5.97c-1.8-5.66-2.93-7.08-6.18-7.08h-1.84v13h-8.78V.55Zm8.78 13.68h2.31c2.83 0 6.42-.1 6.42-3.82 0-3.07-3.06-3.63-6.42-3.63h-2.31v7.46ZM205.27.55h8.64c6.75 0 12.36 2.36 12.36 9.86 0 7.32-5.71 9.91-11.65 9.91h-4.63v13.23h-4.71v-33Zm4.71 15.62h4.67c2.93 0 6.61-1.47 6.61-5.81 0-4.15-4.34-5.66-7.27-5.66h-4v11.47ZM251.13.55h9.72c9.77 0 17.56 6 17.56 16.18 0 11-6.7 16.75-17.56 16.75h-9.72zm4.72 28.79h5c8.12 0 12.6-3.92 12.6-12.41 0-7.7-5.24-12.22-12-12.22h-5.53v24.63ZM281.71.57h4.72v32.94h-4.72zm32.5 5.42a13.25 13.25 0 0 0-6.8-1.84c-7.6 0-12.64 5.48-12.64 12.88 0 7.74 5.57 12.88 12.41 12.88a16.2 16.2 0 0 0 7.17-1.61l.28 4.58a20.6 20.6 0 0 1-7.5 1.18c-10.43 0-17.37-6.7-17.37-17.18 0-10.15 7.37-16.9 17.42-16.9a18.1 18.1 0 0 1 7.4 1.42Zm3.29-3.13a2.67 2.67 0 1 1 2.67 2.67 2.68 2.68 0 0 1-2.67-2.67m4.69 0a2 2 0 1 0-2 2.15 2.06 2.06 0 0 0 2-2.15m-3-1.53h1.12a.89.89 0 0 1 1.11.89.76.76 0 0 1-.79.82l.81 1.32h-.58l-.79-1.3h-.37v1.3h-.56v-3Zm.56 1.3h.49c.34 0 .62 0 .62-.45s-.32-.41-.62-.41h-.5v.86ZM188.53 16.05h12.79v4.31h-12.79zM229.19.57h18.45v4.15h-13.73v9.63h12.5v4.15h-12.5v10.86h13.73v4.15h-18.45z"
        />
    </svg>
);

export const TooltipIcon: IconComponent = (props) => (
    <svg
        className={props.className}
        role="img"
        aria-labelledby={props.title ? props.titleId : undefined}
        aria-hidden={props.ariaHidden ? true : undefined}
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        viewBox="0 0 100 100"
    >
        <Title title={props.title} titleId={props.titleId} />
        <path d="M50 4.25C24.773 4.25 4.25 24.773 4.25 50S24.773 95.75 50 95.75 95.75 75.227 95.75 50 75.227 4.25 50 4.25m4.705 71.305H44.512v-9.513h10.193zm6.621-25.801c-3.008 2.239-6.223 4.058-6.852 8.181v2.66h-9.439v-3.146c.418-6.086 3.285-8.811 6.082-10.979 2.727-2.166 5.104-3.773 5.104-7.551 0-4.266-2.236-6.293-5.941-6.293-5.033 0-7.131 4.125-7.203 9.09H32.799c.211-9.998 6.783-17.27 16.99-17.27 13.215 0 17.412 8.111 17.412 13.492 0 6.784-2.867 9.511-5.875 11.816" />
    </svg>
);

// Wrapper reproducing ``cms/components/svg.html``: the optional
// ``.ada-screenreader-only`` description span SIBLING BEFORE the inline ``<svg>``.
type SvgProps = Immutable<{
    Icon: IconComponent;
    src: string; // kept for parity/documentation; not rendered
    className?: string;
    title?: string;
    description?: string;
    ariaHidden?: boolean;
}>;

export const Svg = (props: SvgProps): React.JSX.Element => {
    const Icon = props.Icon;
    const titleId = React.useId();
    return (
        <>
            {props.description ? (
                <span className="ada-screenreader-only">
                    {props.description}
                </span>
            ) : null}
            <Icon
                className={props.className ?? ""}
                title={props.title || undefined}
                titleId={titleId}
                ariaHidden={Boolean(props.ariaHidden)}
            />
        </>
    );
};
